import { useContext, useMemo, useSyncExternalStore } from 'react';
import {
  useAuth as useOIDCAuth,
  AuthContext as OIDCAuthContext,
} from 'react-oidc-context';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import * as authUtils from '../utils/authUtils';
import bbAxios from '../utils/bbAxios';
import fetchA from '../utils/fetchA';
import { SessionContext } from '../SessionContext';
import { GCPSessionContext } from './useGCPSession';
import { getPermissionsSync, clearPermissions } from '../utils/permissionCache';
import { broadcastLogout } from '../utils/authSync';
import { STORAGE_KEYS } from '../sessionConfig';

/**
 * Subscribe to localStorage changes for live permission updates.
 * @param {Function} callback - Callback to invoke on storage change
 * @returns {Function} Unsubscribe function
 */
const subscribeToPermissions = (callback) => {
  const handler = (e) => {
    if (e.key === STORAGE_KEYS.PERMISSIONS || e.key === null) {
      callback();
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
};

/**
 * Get snapshot of current permissions (serialized for stable reference).
 * @returns {string} Serialized permissions array
 */
const getPermissionsSnapshot = () => {
  const perms = getPermissionsSync();
  return JSON.stringify([...perms].sort());
};

const useAuth = () => {
  const noAuthContext = {
    isAuthenticated: false,
  };
  // Get config, user state, and loginRoute from session context
  const { config, setConfig, user, setUser, loginRoute } = useContext(SessionContext);

  // GCP session context (null when provider is not 'gcp')
  const gcpSession = useContext(GCPSessionContext);

  // Check which auth context is available and use the appropriate hook
  const oidcContext = useContext(OIDCAuthContext);

  // Always call both hooks, but safely handle when contexts don't exist
  let auth = null;
  let auth0 = null;

  // Use Auth0 hook - this won't throw an error, it will just return default values
  auth0 = useAuth0();

  // Only use OIDC hook if the context is available
  if (oidcContext !== undefined) {
    auth = useOIDCAuth();
  }

  const signOut = async () => {
    try {
      if (config?.provider === 'gcp' && gcpSession?.logout) {
        await gcpSession.logout();
      } else if (auth0?.isAuthenticated) {
        console.log('Signing out from Auth0...');
        // Clear local state before the Auth0 redirect (mirrors OIDC logout path)
        clearPermissions();
        broadcastLogout('user_action');
        // Auth0's /v2/logout requires returnTo to be in the app's "Allowed Logout URLs".
        // Use origin instead of the login path — /bbweb/login is typically not whitelisted
        // and causes a 400 "Oops!" error. The SPA router will redirect to login after landing.
        const returnTo = window.location.origin;
        auth0.logout({ logoutParams: { returnTo } });
      } else if (auth?.settings) {
        // Standard OIDC logout
        console.log('Signing out OIDC...');
        const metadataUrl = auth.settings.metadataUrl;
        if (metadataUrl) {
          const {
            data: { end_session_endpoint: endSessionEndpoint },
          } = await axios.get(metadataUrl);
          // Clear OIDC data directly from localStorage INSTEAD of calling
          // auth.removeUser(). removeUser() updates the OIDC React context,
          // triggering a synchronous re-render. Components still mounted on
          // the current page (e.g., PlaybookDetails, DocServer) crash during
          // that re-render accessing auth.user.access_token (now undefined).
          // Clearing localStorage + hard redirect avoids the re-render.
          const { authority, client_id } = auth.settings;
          localStorage.removeItem(`oidc.user:${authority}:${client_id}`);
          clearPermissions();
          broadcastLogout('user_action');
          authUtils.signOutOIDC(endSessionEndpoint, loginRoute);
        } else {
          await auth.removeUser();
        }
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Polyfill auth0 available in the same way as OIDC auth
  authUtils.polyfillAuth0(auth0);

  // Select auth object based on active provider
  const finalAuth =
    config?.provider === 'gcp' ? gcpSession || noAuthContext : auth || auth0 || noAuthContext;

  // Subscribe to localStorage permission changes for cross-tab sync
  const serializedPermissions = useSyncExternalStore(
    subscribeToPermissions,
    getPermissionsSnapshot
  );

  const permissions = useMemo(() => {
    return new Set(JSON.parse(serializedPermissions));
  }, [serializedPermissions]);

  // Permission helper functions
  const permissionHelpers = useMemo(
    () => ({
      /**
       * Check if user has a specific permission
       * @param {string} permission
       * @returns {boolean}
       */
      hasPermission: (permission) => permissions.has(permission),

      /**
       * Check if user has ANY of the specified permissions (OR)
       * @param {string[]} perms
       * @returns {boolean}
       */
      hasAnyPermission: (perms) => perms.some((p) => permissions.has(p)),

      /**
       * Check if user has ALL of the specified permissions (AND)
       * @param {string[]} perms
       * @returns {boolean}
       */
      hasAllPermissions: (perms) => perms.every((p) => permissions.has(p)),
    }),
    [permissions]
  );

  return {
    ...finalAuth,
    auth: finalAuth,
    config,
    setConfig,
    user,
    setUser,
    bbAxios,
    fetchA,
    signOut,
    // Permission properties
    permissions,
    ...permissionHelpers,
  };
};

export default useAuth;
